import {
  FlightsFareTypes,
  FlightSortOptions,
  FlightViewTypes,
  getAdultPassengerState,
  getChildPassengerState,
  getInfantPassengerState,
  getPrimaryPassengerState,
  maxFlightInfantAge,
  maxFlightPassengerCount,
  validFlightDateRangeInMonths,
  FLEXIBILITY_INCLUSIONS,
  FlightsClassTypes,
  titles,
} from 'constants/flight'
import { createSelector } from 'reselect'
import moment from 'moment'
import qs from 'qs'
import { groupBy, last, min, nonNullable, sortByAll, SortField, sum, arrayToObject, unique, max, without, arrayToMap, uniqueBy } from 'lib/array/arrayUtils'
import switchFunc from 'lib/function/switchFunc'
import { FlightDropdownOption, PriceChanges } from 'components/Flights/types'
import { capitalise, contains } from 'lib/string/stringUtils'
import { ISO_DATE_FORMAT, SHORT_DATE_TIME_FORMAT, SHORT_TIME_FORMAT_24_HOUR } from 'constants/dateFormats'
import { formatShortDateRange } from 'tripPlanner/utils'
import { FlightSearchParams } from 'hooks/useSearchFlights'
import getObjectKey from 'lib/object/getObjectKey'
import { FlightSearchWidgetFlightItem } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import { isFlightItem, isInsuranceBookingProtectionItem } from 'lib/checkout/checkoutUtils'
import * as isomorphicLocalStorage from 'lib/storage/isomorphicLocalStorage'
import { FLIGHT_INSURANCE_ID_PREFIX } from 'lib/insurance/insuranceFlights'
import { initialState as filtersInitialState } from 'contexts/Flights/StandaloneFlightsFilters/standaloneFlightsFiltersReducer'
import { FlightsFiltersBagType, FlightsFiltersRecommendedType, FlightsFiltersValues } from './flightFilterTypes'
import React from 'react' // DO NOT REMOVE THIS IMPORT
import BodyText from 'LuxKit/Typography/BodyText'

// fareClassEconomy is an economy fare class
export const fareClassEconomy = 'Economy'
// fareClassBusiness is a business fare class
export const fareClassBusiness = 'Business'

// compareFareClasses is a comparison function to compare two fare classes using a total order
// implemented by the fare class.
function compareFareClasses(a: string, b: string) {
  if (a === b) {
    return 0
  }

  if (a === fareClassEconomy && b !== fareClassEconomy) {
    return -1
  }

  return 1
}

export function getDisplayInfoV2(flights: Array<App.FlightV2>) {
  let bestFareClass = fareClassEconomy
  const fareBookingClasses: Record<string, boolean> = {}
  const flightsWithFreeCheckInBaggage: Record<string, boolean> = {}
  let allFlightsHaveTheSameBaggage = true

  const baggageDetails = {
    freeCarryOnBaggageDescription: flights[0].includedCarryOnBaggage?.description,
    freeCheckedInBaggageDescription: flights[0].includedCheckedBaggage?.description,
    freeCheckedInBaggageIncluded: !!flights[0].includedCheckedBaggage,
  }

  for (let i = 0; i < flights.length; i++) {
    const flight = flights[i]

    fareBookingClasses[flight.bookingClass] = true

    if (flight.includedCheckedBaggage) {
      flightsWithFreeCheckInBaggage[i] = true
    }

    if (allFlightsHaveTheSameBaggage && i > 0) {
      if (baggageDetails.freeCarryOnBaggageDescription !== flight.includedCarryOnBaggage?.description ||
        baggageDetails.freeCheckedInBaggageDescription !== flight.includedCheckedBaggage?.description ||
        baggageDetails.freeCheckedInBaggageIncluded !== !!flight.includedCheckedBaggage
      ) {
        allFlightsHaveTheSameBaggage = false
      }
    }

    if (compareFareClasses(flight.fareClass, bestFareClass) > 0) {
      bestFareClass = flight.fareClass
    }
  }

  return {
    hasMultipleFareBookingClasses: Object.keys(fareBookingClasses).length > 1,
    bestFareClass,
    baggageIncluded: Object.keys(flightsWithFreeCheckInBaggage).length === flights.length,
    allFlightsHaveTheSameBaggage,
  }
}

function applyJourneyFilters<T extends App.AnyJourney>(journeys: Array<T>, filters: Array<App.FlightFilter>) {
  return journeys.map(journey => {
    const isHiddenByFilters = !filters.every(filter => {
      const activeOptions = filter.options.filter(option => option.active)
      const isAllOptionActive = activeOptions.find(ao => ao.value === 'all')
      if (isAllOptionActive) {
        return true
      }
      return activeOptions.some(af => filter.fn(journey, af.value))
    })
    return ({
      ...journey,
      hidden: isHiddenByFilters,
    })
  })
}

export const getJourneys = {
  departing: createSelector(
    (state: App.State) => state.flights.journeys,
    (state: App.State) => state.flights.flightSearchV2?.onewayFares,
    (journeys, oneWayFares): Array<App.AnyJourney> => oneWayFares?.[0] ?? journeys,
  ),
  returning: createSelector(
    (state: App.State) => state.flights.journeys,
    (state: App.State) => state.flights.flightSearchV2?.onewayFares,
    (journeys, oneWayFares): Array<App.AnyJourney> => oneWayFares?.[1] ?? journeys,
  ),
}

const getFilteredDepartingJourneys = createSelector(
  (state: App.State) => getJourneys.departing(state),
  (state: App.State) => state.flights.filters,
  (journeys, filters) => applyJourneyFilters(journeys, filters),
)

const flightPriceSort: SortField<App.Journey> = { selector: j => j.cost, direction: 'asc' }

const departingFlightStopoverSort: SortField<App.Journey> = { selector: j => j.departing.flights.length, direction: 'asc' }
const departingFlightTimeSort: SortField<App.Journey> = { selector: j => j.departing.flightTime ? convertTimeObjectToMinutes(j.departing.flightTime) : undefined, direction: 'asc' }
const departingDepartureSort: SortField<App.Journey> = { selector: j => +new Date(`${j.departing.flights[0].departingDate}T${j.departing.flights[0].departingTime}`), direction: 'asc' }
const departingDepartureDescSort: SortField<App.Journey> = { selector: j => +new Date(`${j.departing.flights[0].departingDate}T${j.departing.flights[0].departingTime}`), direction: 'desc' }
const departingArrivalSort: SortField<App.Journey> = { selector: j => +new Date(`${last(j.departing.flights).arrivalDate}T${last(j.departing.flights).arrivalTime}`), direction: 'asc' }

const returningFlightStopoverSort: SortField<App.Journey> = { selector: j => j.returning?.flights.length, direction: 'asc' }
const returningFlightTimeSort: SortField<App.Journey> = { selector: j => j.returning?.flightTime ? convertTimeObjectToMinutes(j.returning.flightTime) : undefined, direction: 'asc' }
const returningDepartureSort: SortField<App.Journey> = { selector: j => j.returning ? +new Date(`${j.returning.flights[0].departingDate}T${j.returning.flights[0].departingTime}`) : undefined, direction: 'asc' }
const returningDepartureDescSort: SortField<App.Journey> = { selector: j => j.returning ? +new Date(`${j.returning.flights[0].departingDate}T${j.returning.flights[0].departingTime}`) : undefined, direction: 'desc' }
const returningArrivalSort: SortField<App.Journey> = { selector: j => j.returning ? +new Date(`${last(j.returning.flights).arrivalDate}T${last(j.returning.flights).arrivalTime}`) : undefined, direction: 'asc' }

export const getDepartingSortList = switchFunc<Array<SortField<App.Journey>>>({
  [FlightSortOptions.Best]: [departingFlightStopoverSort, flightPriceSort, departingFlightTimeSort, departingDepartureSort],
  [FlightSortOptions.Cheapest]: [flightPriceSort, departingFlightStopoverSort, departingFlightTimeSort, departingDepartureSort],
  [FlightSortOptions.Fastest]: [departingFlightTimeSort, flightPriceSort, departingDepartureSort, departingFlightTimeSort],
  [FlightSortOptions.EarliestTakeOff]: [departingDepartureSort, flightPriceSort, departingFlightStopoverSort, departingFlightTimeSort],
  [FlightSortOptions.LatestTakeOff]: [departingDepartureDescSort, flightPriceSort, departingFlightStopoverSort, departingFlightTimeSort],
  [FlightSortOptions.EarliestLanding]: [departingArrivalSort, flightPriceSort, departingFlightStopoverSort, departingFlightTimeSort],
}, [flightPriceSort, departingFlightStopoverSort, departingFlightTimeSort, departingDepartureSort, departingDepartureDescSort, departingArrivalSort])

const flightPriceSortV2: SortField<App.JourneyV2> = {
  selector: j => j.fareType === FlightViewTypes.RETURN ? j.price.all.totalRoundTripPrice : j.price.all.totalFare,
  direction: 'asc',
}

const departingFlightStopoverSortV2: SortField<App.JourneyV2> = { selector: j => j.flightGroup.flights.length, direction: 'asc' }
const departingFlightTimeSortV2: SortField<App.JourneyV2> = { selector: j => convertTimeObjectToMinutes(j.flightGroup.totalFlightDuration), direction: 'asc' }
const departingDepartureSortV2: SortField<App.JourneyV2> = { selector: j => +new Date(`${j.flightGroup.flights[0].departingDate}T${j.flightGroup.flights[0].departingTime}`), direction: 'asc' }
const departingDepartureDescSortV2: SortField<App.JourneyV2> = { selector: j => +new Date(`${j.flightGroup.flights[0].departingDate}T${j.flightGroup.flights[0].departingTime}`), direction: 'desc' }
const departingArrivalSortV2: SortField<App.JourneyV2> = { selector: j => +new Date(`${last(j.flightGroup.flights).arrivalDate}T${last(j.flightGroup.flights).arrivalTime}`), direction: 'asc' }

const returningFlightStopoverSortV2: SortField<App.JourneyV2> = { selector: j => j.flightGroup.flights.length, direction: 'asc' }
const returningFlightTimeSortV2: SortField<App.JourneyV2> = { selector: j => convertTimeObjectToMinutes(j.flightGroup.totalFlightDuration), direction: 'asc' }
const returningDepartureSortV2: SortField<App.JourneyV2> = { selector: j => +new Date(`${j.flightGroup.flights[0].departingDate}T${j.flightGroup.flights[0].departingTime}`), direction: 'asc' }
const returningDepartureDescSortV2: SortField<App.JourneyV2> = { selector: j => +new Date(`${j.flightGroup.flights[0].departingDate}T${j.flightGroup.flights[0].departingTime}`), direction: 'desc' }
const returningArrivalSortV2: SortField<App.JourneyV2> = { selector: j => +new Date(`${last(j.flightGroup.flights).arrivalDate}T${last(j.flightGroup.flights).arrivalTime}`), direction: 'asc' }

export const getDepartingSortListV2 = switchFunc<Array<SortField<App.JourneyV2>>>({
  [FlightSortOptions.Best]: [departingFlightStopoverSortV2, flightPriceSortV2, departingFlightTimeSortV2, departingDepartureSortV2],
  [FlightSortOptions.Cheapest]: [flightPriceSortV2, departingFlightStopoverSortV2, departingFlightTimeSortV2, departingDepartureSortV2],
  [FlightSortOptions.Fastest]: [departingFlightTimeSortV2, flightPriceSortV2, departingDepartureSortV2, departingFlightTimeSortV2],
  [FlightSortOptions.EarliestTakeOff]: [departingDepartureSortV2, flightPriceSortV2, departingFlightStopoverSortV2, departingFlightTimeSortV2],
  [FlightSortOptions.LatestTakeOff]: [departingDepartureDescSortV2, flightPriceSortV2, departingFlightStopoverSortV2, departingFlightTimeSortV2],
  [FlightSortOptions.EarliestLanding]: [departingArrivalSortV2, flightPriceSortV2, departingFlightStopoverSortV2, departingFlightTimeSortV2],
}, [flightPriceSortV2, departingFlightStopoverSortV2, departingFlightTimeSortV2, departingDepartureSortV2, departingDepartureDescSortV2, departingArrivalSortV2])

export const getReturningSortListV2 = switchFunc<Array<SortField<App.JourneyV2>>>({
  [FlightSortOptions.Best]: [returningFlightStopoverSortV2, flightPriceSortV2, returningFlightTimeSortV2, returningDepartureSortV2],
  [FlightSortOptions.Cheapest]: [flightPriceSortV2, returningFlightStopoverSortV2, returningFlightTimeSortV2, returningDepartureSortV2],
  [FlightSortOptions.Fastest]: [returningFlightTimeSortV2, flightPriceSortV2, returningDepartureSortV2, returningFlightTimeSortV2],
  [FlightSortOptions.EarliestTakeOff]: [returningDepartureSortV2, flightPriceSortV2, returningFlightStopoverSortV2, returningFlightTimeSortV2],
  [FlightSortOptions.LatestTakeOff]: [returningDepartureDescSortV2, flightPriceSortV2, returningFlightStopoverSortV2, returningFlightTimeSortV2],
  [FlightSortOptions.EarliestLanding]: [returningArrivalSortV2, flightPriceSortV2, returningFlightStopoverSortV2, returningFlightTimeSortV2],
}, [flightPriceSortV2, returningFlightStopoverSortV2, returningFlightTimeSortV2, returningDepartureSortV2, returningDepartureDescSortV2, returningArrivalSortV2])

export function getJourneysSortList(option: FlightSortOptions, segment: 'departing' | 'returning', isV2?: boolean) {
  if (segment === 'departing') {
    return isV2 ? getDepartingSortListV2(option) : getDepartingSortList(option)
  } else {
    return isV2 ? getReturningSortListV2(option) : getReturningSortList(option)
  }
}

export function getUniqueDepartingFlights(journeys: Array<App.Journey>) {
  const grouped = groupBy(journeys, j => j.departing.journeyKey)
  return Array.from(grouped.values()).map(group => {
    const visibleJourneys = group.filter(j => !j.hidden)
    return min(visibleJourneys.length === 0 ? group : visibleJourneys, val => val.cost)
  })
}

// getDepartingJourneys returns an array of journeys with unique departing journey key.
// Concretely, it de-duplicates journeys by its 'departing' object's 'journeyKey'. Any tie is
// broken by choosing the object from the journey with a lower cost.
export const getDepartingJourneys = createSelector(
  (state: App.State) => getFilteredDepartingJourneys(state),
  (state: App.State) => state.flights.sort,
  (journeys: Array<App.AnyJourney>, sortValue) => {
    if (isJourneyV2Items(journeys)) {
      const visibleJourneys = journeys.filter((journey) => !journey.hidden)
      return sortByAll(nonNullable(visibleJourneys), getDepartingSortListV2(sortValue))
    }
    const uniqueJourneys = getUniqueDepartingFlights(journeys as Array<App.Journey>)

    return sortByAll(nonNullable(uniqueJourneys), getDepartingSortList(sortValue))
  },
)

export const getReturningSortList = switchFunc<Array<SortField<App.Journey>>>({
  [FlightSortOptions.Best]: [returningFlightStopoverSort, flightPriceSort, returningFlightTimeSort, returningDepartureSort],
  [FlightSortOptions.Cheapest]: [flightPriceSort, returningFlightStopoverSort, returningFlightTimeSort, returningDepartureSort],
  [FlightSortOptions.Fastest]: [returningFlightTimeSort, flightPriceSort, returningDepartureSort, returningFlightTimeSort],
  [FlightSortOptions.EarliestTakeOff]: [returningDepartureSort, flightPriceSort, returningFlightStopoverSort, returningFlightTimeSort],
  [FlightSortOptions.LatestTakeOff]: [returningDepartureDescSort, flightPriceSort, returningFlightStopoverSort, returningFlightTimeSort],
  [FlightSortOptions.EarliestLanding]: [returningArrivalSort, flightPriceSort, returningFlightStopoverSort, returningFlightTimeSort],
}, [flightPriceSort, returningFlightStopoverSort, returningFlightTimeSort, returningDepartureSort, returningDepartureDescSort, returningArrivalSort])

export function tripType(journey: App.Journey) {
  return journey.departing.flights[0].departingDisplayNames.departingCountry === journey.returning?.flights[0].departingDisplayNames.departingCountry ?
    'domestic' : 'international'
}

const createPrimary = (): App.Passenger => {
  const passenger = getPrimaryPassengerState()
  return {
    id: 'adult-0',
    type: 'adult',
    ...passenger,
  }
}

const createAdult = (index: number): App.Passenger => {
  const passenger = getAdultPassengerState()
  return {
    id: `adult-${index}`,
    type: 'adult',
    ...passenger,
  }
}

const createChild = (index: number): App.Passenger => {
  const passenger = getChildPassengerState()
  return {
    id: `child-${index}`,
    type: 'child',
    ...passenger,
  }
}

const createInfant = (index: number): App.Passenger => {
  const passenger = getInfantPassengerState()
  return {
    id: `infant-${index}`,
    type: 'infant',
    ...passenger,
  }
}

function createPassengerArray(passengerCreator: (num: number) => App.Passenger, length: number, baseIndex: number): Array<App.Passenger> {
  const arr: Array<App.Passenger> = []
  for (let i = 0; i < length; i++) {
    arr.push(passengerCreator(baseIndex + i))
  }

  return arr
}

/**
 * Build list of pax for the new checkout cart item state
 *
 * @remarks
 * Main difference from "createPassengers()" is that the `id` suffix starts at 1 instead of 0
 *   i.e. 'adult-1'
 */
export function newCheckoutCreatePassengers({ adults, children = 0, infants = 0 }: App.Occupants) {
  const adultPaxList: Array<App.Passenger> = []
  const childPaxList: Array<App.Passenger> = []
  const infantList: Array<App.Passenger> = []

  for (let i = 0; i < adults; i++) { adultPaxList.push(createAdult(i + 1)) }
  for (let i = 0; i < children; i++) { childPaxList.push(createChild(i + 1)) }
  for (let i = 0; i < infants; i++) { infantList.push(createInfant(i + 1)) }

  return [...adultPaxList, ...childPaxList, ...infantList]
}

export function createPassengers({ adults, children, infants }: App.Occupants) {
  const primary = createPrimary()
  const mappedAdults = createPassengerArray(createAdult, adults - 1, 1)
  const mappedChildren = createPassengerArray(createChild, (children ?? 0), adults)
  const mappedInfants = createPassengerArray(createInfant, (infants ?? 0), adults + (children ?? 0))

  return [
    primary,
    ...mappedAdults,
    ...mappedChildren,
    ...mappedInfants,
  ]
}

function restorePassengerWithBaggage(storedPassenger: App.Passenger, index: number) {
  if (storedPassenger.isPrimary) {
    return createPrimary()
  } else if (storedPassenger.type === 'adult') {
    return createAdult(index)
  } else if (storedPassenger.type === 'child') {
    return createChild(index)
  } else {
    return createInfant(index)
  }
}

export function restorePassengersWithBaggage(storedPassengers: Array<App.Passenger>) {
  if (!storedPassengers.some(p => p.isPrimary)) {
    return undefined // Something's screwed up, we're missing a primary passenger
  }

  return storedPassengers.map((passenger, index) => ({
    ...restorePassengerWithBaggage(passenger, index),
    ...passenger,
  }))
}

export function parseContactNumber(number: App.PhoneNumber) {
  // attempt to remove any code from the phone input just in case the user entered
  // the code into the phone input as well as selecting a code
  return `${number.prefix}${number.phone.replace(new RegExp(`^([+]?${number.prefix}|0+)`), '')}`
}

function getPassengerAddedBaggage(passengers: Array<App.Passenger>, journeyId: string): Array<Array<App.AddedBaggage>> {
  return nonNullable(passengers.map(p => {
    if (p?.addedExtras?.baggage?.[journeyId]) {
      return p.addedExtras.baggage[journeyId]
    }
    return []
  }))
}

export function getBaggageCount(passengers: Array<App.Passenger>, journeySegment: App.JourneyFlight) {
  const journeyId = journeySegment.journeyKey
  const passengerAddedBaggage = getPassengerAddedBaggage(passengers, journeyId)
  return sum(passengerAddedBaggage, passengerBaggage => sum(passengerBaggage, o => o.count))
}

export function getBaggageCost(passengers: Array<App.Passenger>, journeySegment: App.JourneyFlight) {
  const journeyId = journeySegment.journeyKey
  const passengerAddedBaggage = getPassengerAddedBaggage(passengers, journeyId)
  return sum(passengerAddedBaggage, (passengerBaggage) => (
    sum(passengerBaggage, selectedOption => {
      const option = journeySegment.extras?.baggage?.find(availableOption => availableOption.id === selectedOption.id)
      return option ? selectedOption.count * option.amount : 0
    })
  ))
}

export function resetFilter(filter: App.FlightFilter) {
  return {
    ...filter,
    options: filter.options.map(o => ({
      ...o,
      active: !!o.isDefault,
    })),
  }
}

export function getFlightCalendarKey(props: { startDate: string, endDate: string, origin: string, destination: string, nights?: number, region: string, currency: string }) {
  return `${props.startDate}-${props.endDate}-${props.origin}-${props.destination}-${props.nights}-${props.region}-${props.currency}`
}

export function updateFilter(filter: App.FlightFilter, newOption: App.FlightFilterOption, selectedOptionOnly: boolean) {
  return {
    ...filter,
    options: filter.options.map(o => {
      // If "select only" option select => only that should be select
      // If "all" select => de-select all other options
      // If any other option select => de-select "all" option
      // all option & other option can't select at one time

      if (selectedOptionOnly) {
        return {
          ...o,
          active: o === newOption,
        }
      }

      if (newOption.value === 'all') {
        return {
          ...o,
          active: o === newOption ? !o.active : false,
        }
      }

      if (o.value === 'all') {
        return {
          ...o,
          active: false,
        }
      } else if (o === newOption) {
        return {
          ...o,
          active: !o.active,
        }
      }

      return o
    }),
  }
}

export function getSortingLabel(selectedOption?: FlightSortOptions) {
  switch (selectedOption) {
    case FlightSortOptions.EarliestTakeOff:
      return {
        sortingTitle: 'Take-off:',
        sortingLabel: 'Earliest',
      }
    case FlightSortOptions.LatestTakeOff:
      return {
        sortingTitle: 'Take-off:',
        sortingLabel: 'Latest',
      }
    case FlightSortOptions.EarliestLanding:
      return {
        sortingTitle: 'Landing:',
        sortingLabel: 'Earliest',
      }
    default:
      return {
        sortingLabel: 'Other',
      }
  }
}

export function getOtherSortOptions(originCode: string, destinationCode: string): Array<FlightDropdownOption<FlightSortOptions>> {
  const takeOffAirport = originCode
  const landingAirport = destinationCode

  return [{
    value: FlightSortOptions.EarliestTakeOff,
    label: `Earliest take-off (${takeOffAirport})`,
  }, {
    value: FlightSortOptions.LatestTakeOff,
    label: `Latest take-off (${takeOffAirport})`,
  }, {
    value: FlightSortOptions.EarliestLanding,
    label: `Earliest landing (${landingAirport})`,
  }]
}

export function filterAirlinesByCarrier(filters: Array<App.FlightFilter>, carrier: string): App.FlightFilter | undefined {
  const airlineFilter = filters.find(filter => filter.id === 'airline')

  if (airlineFilter) {
    return {
      ...airlineFilter,
      options: airlineFilter.options.map(option => ({
        ...option,
        active: option.value === carrier,
      })),
    }
  }

  return undefined
}

export function calcPriceChanges(departingPriceSnapshot: number, journeyCost: number) {
  const priceChanges: PriceChanges = {
    wentUp: false,
    stayedTheSame: true,
    amount: 0,
  }

  if (journeyCost > departingPriceSnapshot) {
    priceChanges.wentUp = true
    priceChanges.stayedTheSame = false
    priceChanges.amount = (journeyCost - departingPriceSnapshot)
  }

  if (journeyCost < departingPriceSnapshot) {
    priceChanges.wentDown = true
    priceChanges.stayedTheSame = false
    priceChanges.amount = (departingPriceSnapshot - journeyCost)
  }

  if (journeyCost === departingPriceSnapshot) {
    priceChanges.wentUp = false
    priceChanges.wentDown = false
    priceChanges.stayedTheSame = true
    priceChanges.amount = 0
  }

  return priceChanges
}

export function calcPriceDiff(departingPriceSnapshot: number, journeyCost: number): PriceChanges {
  return {
    amount: journeyCost - departingPriceSnapshot,
  }
}

export function getFareClass(flightClass: string) {
  if (contains(flightClass, 'biz') || contains(flightClass, 'business')) {
    return 'Business'
  } else if (contains(flightClass, 'first')) {
    return 'First'
  } else if (contains(flightClass, 'premium')) {
    return 'Premium'
  }
  return 'Economy'
}

export function getAirportDisplayText(airport?: App.AirportLocation) {
  if (!airport) {
    return undefined
  }

  const {
    cityCode,
    isMultiAirport,
    isChildDisplay,
    airportName,
    airportCode,
    cityAirportName,
  } = airport

  const isCityAirport = isMultiAirport && isChildDisplay
  const displayName = isCityAirport ? cityAirportName : airportName
  const displayCode = isCityAirport ? cityCode : airportCode

  return `${displayName} (${displayCode})`
}

export function getAirportDisplayTextDropdown(airport?: App.AirportLocation) {
  if (!airport) {
    return undefined
  }

  const {
    cityCode,
    isMultiAirport,
    isChildDisplay,
    airportName,
    airportCode,
    cityAirportName,
  } = airport

  const isCityAirport = isMultiAirport && isChildDisplay
  const displayName = isCityAirport ? cityAirportName : airportName
  const displayCode = isCityAirport ? cityCode : airportCode

  if (airport.isMultiAirport) {
    return displayName
  }

  return React.createElement(BodyText, { variant: 'medium' },
    displayName + ' ',
    React.createElement(BodyText, { variant: 'medium', colour: 'neutral-three', as: 'span' }, displayCode),
  )
}

export function validateFlightOccupants(occupants: App.Occupants) {
  const totalPassengers = occupants.adults + (occupants?.children ?? 0)
  const totalInfants = occupants?.childrenAge?.filter((age) => age !== -1 && age <= maxFlightInfantAge).length ?? 0
  return totalPassengers > maxFlightPassengerCount || totalInfants > occupants.adults
}

export function mapAirportToAirportLocation(airport: App.Airport): App.AirportLocation {
  return {
    airportCode: airport.code,
    airportName: airport.name,
  }
}

export function isOneWayFlight(fareType: string): boolean {
  return !!fareType && fareType === FlightsFareTypes.ONE_WAY
}

export interface StandaloneFlightSearchParam {
  originAirportCode: string
  originAirportName: string
  destinationAirportCode: string
  destinationAirportName: string
  departDate: string
  returnDate: string | null
  adults: number
  children: number
  infants: number
  /** Comma-separated list of ages */
  childrenAge?: string
  fareClass: string
  fareType: 'return' | 'oneWay' | 'multiCity'
}

export function buildStandaloneFlightSearchUrl(params: StandaloneFlightSearchParam) {
  return `/flights-search-results?${qs.stringify(params)}`
}

export function deriveFareType(hasSecondLegWithDate: boolean, mapped: FlightsFareTypes): FlightsFareTypes {
  if (!hasSecondLegWithDate) return FlightsFareTypes.ONE_WAY
  if (mapped === FlightsFareTypes.MULTI_CITY) return FlightsFareTypes.MULTI_CITY
  return FlightsFareTypes.RETURN
}

export function getFlightDealDuration(travelPeriods: Array<App.FlightDealTravelPeriod>) {
  const earliestDate = min(travelPeriods, period => +new Date(period.departureDate))?.departureDate
  const latestDate = max(travelPeriods, period => +new Date(period.arrivalDate))?.arrivalDate

  return formatShortDateRange({ startDate: moment(earliestDate), endDate: moment(latestDate) })
}

export function getOperatingCarrierText(flights: Array<App.Flight>) {
  const differentOperatingFlights = flights.filter(flight => flight.carrier !== flight.operatingCarrier)

  if (differentOperatingFlights.length !== 0) {
    return differentOperatingFlights[0].operatedByText
  }
  return null
}

export function filterAirportRecords(
  records: Array<App.AirportLocation>,
  excludingAirport?: App.AirportLocation,
  noParentAirports?: boolean
) {
  if (records) {
    const excludingRecord = records.find(record => record.airportCode === excludingAirport?.airportCode)
    const airportsWithoutSelections = without(records, excludingRecord)
    const groupedAirportRecords = groupBy(airportsWithoutSelections, record => record?.cityCode)

    if (noParentAirports) {
      return airportsWithoutSelections.filter(airport => !airport.isMultiAirport)
    }

    return nonNullable(Array.from(groupedAirportRecords.entries()).map(groupedAirports =>
      groupedAirports[1].map((airport, index) => {
        if (!airport) return
        return {
          ...airport,
          isCityAirport: index !== 0 && !!groupedAirports[1].find(airport => airport?.isMultiAirport),
        }
      }),
    ).flat())
  }
  return []
}

export function isJourneyV2Items(items: Array<App.AnyJourney>): items is Array<App.JourneyV2> {
  return isJourneyV2(items?.[0])
}

export function isJourneyV1Items(items: Array<App.AnyJourney>): items is Array<App.Journey> {
  return isJourneyV1(items?.[0])
}

export function isJourneyV2(item?: App.AnyJourney): item is App.JourneyV2 {
  return item?.itemType === JOURNEY_TYPE_V2
}

export function isJourneyV1(item?: App.AnyJourney): item is App.Journey {
  return item?.itemType === JOURNEY_TYPE_V1
}

export function isJourneyV2ReturnFares(item?: App.AnyJourney) {
  return isJourneyV2(item) && item.fareType === FlightViewTypes.RETURN
}

export function isJourneyV2ReturnFaresItems(items: Array<App.AnyJourney>) {
  return items[0]?.fareType === FlightViewTypes.RETURN
}

export function getBaggageCostV2(passengers: Array<App.Passenger>, journey: App.JourneyV2) {
  const journeyId = journey.bookingInfo.journeyKey
  const passengerAddedBaggage = getPassengerAddedBaggage(passengers, journeyId)
  const flight = journey.flightGroup.flights[0]

  return sum(passengerAddedBaggage, (passengerBaggage) => (
    sum(passengerBaggage, selectedOption => {
      const option = flight.checkedBaggageOptions.find(availableOption => availableOption.id === selectedOption.id)
      return option ? selectedOption.count * option.amount : 0
    })
  ))
}

export function getBaggageCountV2(passengers: Array<App.Passenger>, journeyV2: App.JourneyV2) {
  const passengerAddedBaggage = getPassengerAddedBaggage(passengers, journeyV2.bookingInfo.journeyKey)
  return sum(passengerAddedBaggage, passengerBaggage => sum(passengerBaggage, o => o.count))
}

export const JOURNEY_TYPE_V1 = 'journeyV1'
export const JOURNEY_TYPE_V2 = 'journeyV2'

export function getAirlineLogoAndCarriers(carrierNames: string, carrierLogos: string) {
  const uniqueCarrierLogos = unique(carrierLogos.split('|'))
  const uniqueCarrierNames = unique(carrierNames.split('|'))

  return [uniqueCarrierLogos, uniqueCarrierNames]
}

export function getMainAirlineLogo(carrierLogos?: string) {
  return carrierLogos?.split('|')?.[0]
}

export function compareAirportCodes(originAirport: string, destinationAirport: string) {
  const regex = new RegExp('ALL$')
  return originAirport.replace(regex, '') === destinationAirport.replace(regex, '')
}

export function filterSameDayFares(outboundFare: App.JourneyV2, inboundFares: Array<App.JourneyV2>) {
  const lastOutboundFlight = last(outboundFare.flightGroup.flights)
  // 5 Hours is for ticketing time and change overtime after testing it would become 3
  const arrivalTime = moment(lastOutboundFlight.arrivalTime, SHORT_TIME_FORMAT_24_HOUR).add(5, 'hours')

  return inboundFares.filter((fare) => {
    const firstInboundFlight = fare.flightGroup.flights[0]
    const departingTime = moment(firstInboundFlight.departingTime, SHORT_TIME_FORMAT_24_HOUR)
    return departingTime.isAfter(arrivalTime)
  })
}

export function calcTotalDisplayPrice(isDeparting: boolean, journeyPrice: number, departingFarePrice?: number) {
  if (isDeparting) return null
  return journeyPrice + (departingFarePrice ?? 0)
}

export function calcBundledTotalDisplayPrice(journeyPrice: number, departingFarePrice?: number) {
  return journeyPrice + (departingFarePrice ?? 0)
}

export function getJourneyV2IdKey(
  journeyId: string,
  searchId: string,
  fareFamilyId?: string,
) {
  return getObjectKey({
    journeyId,
    searchId,
    fareFamilyId,
  })
}

export function getDateChangeJourneyV2IdKey(
  reservationId: string,
  searchId: string,
  journeyId: string,
) {
  return getObjectKey({
    reservationId,
    searchId,
    journeyId,
  })
}

export function getFlightSeatsKey(searchId: string, fareIds: Array<string>, passengerCount: number) {
  return getObjectKey({
    searchId,
    fareIds: fareIds.join(','),
    passengers: passengerCount,
  })
}

export interface FlightPriceKey {
  numberOfAdults: number
  numberOfChildren: number
  numberOfInfants: number
  startDate: string
  endDate: string
  nights?: number
  origin?: string
  destination: string
}

export function getFlightPriceKey(props: FlightPriceKey) {
  return `${props.numberOfAdults}-${props.numberOfChildren}-${props.numberOfInfants}-${props.startDate}-${props.endDate}-${props.nights}-${props.origin}-${props.destination}`
}

interface FlightKeyParams extends FlightSearchParams {
  currency: string
  key?: string | number
}

export function getFlightSearchKey(params: FlightKeyParams) {
  const flightsKeys = params.itinerary.map(it => [it.departureDate, it.departureAirport, it.arrivalAirport].join('-'))

  return [
    ...flightsKeys,
    params.currency,
    serialiseFlightsOccupancy(params.occupancies),
    params.fareClass,
    params.maxArrivalTime,
    params.minReturningDepartureTime,
    params.carriers?.join(','),
    params.fareType,
    params.viewType,
    params.key,
    params.filterAirlines?.join(','),
    params.source,
    params.charterSearchId,
  ].join(':')
}

export function convertTimeObjectToMinutes(timeObject?: { hours: number, minutes: number }) {
  if (!timeObject) {
    return 0
  }
  return timeObject.hours * 60 + timeObject.minutes
}

export function convertTimeStringToMinutes(timeString: string) {
  const [hours, minutes] = timeString.split(':')

  return parseInt(hours, 10) * 60 + parseInt(minutes, 10)
}

export function isFlightDatesBetweenValidRange(startDate: string, endDate: string) {
  const now = moment()
  const duration = moment.duration(moment(startDate).diff(now)).asMonths()
  return (duration < validFlightDateRangeInMonths) && (!moment(startDate).isSame(endDate))
}

export function convertMinutesToHoursAndMinutes(totalMinutes: number) {
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return { hours, minutes }
}

export function convertTimeV2StringToMinutes(dateTimeString: string) {
  const timeString = last(dateTimeString.split('-'))
  const [hours, minutes] = timeString.split(':')

  return parseInt(hours, 10) * 60 + parseInt(minutes, 10)
}

export function convertMinutesToTimeString(minutes: number) {
  const time = convertMinutesToHoursAndMinutes(minutes)
  const hoursStr = time.hours.toString()
  const minutesStr = time.minutes.toString()
  return `${hoursStr.padStart(2, '0')}:${minutesStr.padStart(2, '0')}`
}

export function getBaggageDescription(
  baggage: Array<Array<App.AddedBaggage>> = [],
  options: Array<App.FlightV2BaggageOption> = [],
): string | undefined {
  const allBags = baggage.flatMap(bags => bags).filter(bag => bag.count !== 0)

  if (allBags.length === 0) {
    return undefined
  }
  const dictCounts = groupBy(allBags, bag => bag.id)
  // everything using this data assumes its an object at the moment (not a map), put it back to an object
  const baggageIdWithCount = arrayToObject(dictCounts.entries(), ([key]) => key, ([_, bags]) => sum(bags, bag => bag.count))
  const optionsById = arrayToObject(options, option => option.id)
  const baggageDescriptions = Object.entries(baggageIdWithCount).map(([id, count]) => `${count}x ${optionsById[id]?.description}`)
  return baggageDescriptions.join(', ')
}

export function getAddedBaggage(flightDetails?: App.FlightOrderItemDetails): App.Checkout.PaxBaggageMap | undefined {
  return flightDetails?.travellers.reduce<App.Checkout.PaxBaggageMap>((acc, traveller, index) => {
    const travellerId = `${traveller.type}_${index}`
    acc[`${travellerId}`] = {}
    traveller.baggage?.forEach((baggageItem: any) => {
      if (baggageItem.departing_count > 0) {
        acc[travellerId][`${baggageItem.baggage_type}`] = baggageItem.departing_count
      } else if (baggageItem.returning_count > 0) {
        acc[travellerId][`${baggageItem.baggage_type}`] = baggageItem.returning_count
      }
    })
    return acc
  }, {})
}

export function getAddedBaggageFromAddons(addons: Array<App.LegAddon>) {
  return addons
    .filter(addon => addon.type === 'checked_baggage')
    .reduce<Record<string, number>>((acc, addon) => {
      acc[addon.description] = (acc[addon.description] || 0) + addon.quantity
      return acc
    }, {})
}

export function getLegInclusions(inclusions: Array<App.LegInclusion>) {
  return inclusions
    .filter(inclusion => inclusion.status === 'INCLUDED')
}

const defaultTime = {
  hours: 0,
  minutes: 0,
}

export function getFlightV2BestPriceAndTime(journeys: Array<App.JourneyV2>, sortOption: FlightSortOptions): { time: { hours: number, minutes: number }, price: number } {
  const sortedFlight = sortByAll(journeys, getDepartingSortListV2(sortOption))[0]

  return {
    price: sortedFlight?.price.all.totalFare || 0,
    time: sortedFlight?.flightGroup.totalFlightDuration || defaultTime,
  }
}

export function getNextFlightFilters(name: keyof FlightsFiltersValues, value: string, filters: FlightsFiltersValues) {
  let nextFilter: FlightsFiltersValues = { ...filters }

  if (value === 'all' && Array.isArray(filters[name])) {
    nextFilter = {
      ...nextFilter,
      [name]: filtersInitialState[name],
    }
    nextFilter = updateDirectFlightsFilterValues(nextFilter, name)
    return nextFilter
  }

  const existingFilters = filters[name] || []
  const isCheckbox = Array.isArray(filters[name])

  if (isCheckbox) {
    if (!filters[name]?.includes(value)) {
      nextFilter = {
        ...nextFilter,
        [name]: [...existingFilters, value],
      }
    } else if (Array.isArray(existingFilters)) {
      nextFilter = {
        ...nextFilter,
        [name]: without(existingFilters, value),
      }
    }

    nextFilter = updateDirectFlightsFilterValues(nextFilter, name)
    return nextFilter
  }

  return {
    ...nextFilter,
    [name]: value,
  }
}

const updateDirectFlightsFilterValues = (filters: FlightsFiltersValues, targetName: string): FlightsFiltersValues => {
  const nextFilter: FlightsFiltersValues = { ...filters }

  if (targetName === 'recommended') {
    if (nextFilter.recommended?.includes(FlightsFiltersRecommendedType.DIRECT_FLIGHTS)) {
      nextFilter.stops = ['0']
    } else {
      nextFilter.stops = []
    }

    if (nextFilter.recommended?.includes(FlightsFiltersRecommendedType.INCLUDES_CHECKED_BAG)) {
      nextFilter.bags = [FlightsFiltersBagType.CHECKED]
    } else {
      nextFilter.bags = []
    }
  }

  if (targetName === 'bags' && Array.isArray(nextFilter.recommended)) {
    if (nextFilter.bags?.includes(FlightsFiltersBagType.CHECKED)) {
      nextFilter.recommended = [...new Set([...(nextFilter.recommended || []), FlightsFiltersRecommendedType.INCLUDES_CHECKED_BAG])]
    } else {
      nextFilter.recommended = nextFilter.recommended?.filter(filter => filter !== FlightsFiltersRecommendedType.INCLUDES_CHECKED_BAG) || []
    }
  }

  if (targetName === 'stops' && Array.isArray(nextFilter.recommended)) {
    if (nextFilter.stops?.length === 1 && nextFilter.stops?.includes('0')) {
      nextFilter.recommended = [...new Set([...(nextFilter.recommended || []), FlightsFiltersRecommendedType.DIRECT_FLIGHTS])]
    } else {
      nextFilter.recommended = nextFilter.recommended?.filter(filter => filter !== FlightsFiltersRecommendedType.DIRECT_FLIGHTS) || []
    }
  }

  return nextFilter
}

export function hasNoFlexibilityFareFeatures(features: Array<App.FareFamilyFeature>) {
  return features.filter(feature => !!feature.type).every(feature => !FLEXIBILITY_INCLUSIONS.has(feature.type!))
}

export function getFlightV2BestPrice(journeys: Array<App.JourneyV2>): number | undefined {
  const sortedFlight = sortByAll(journeys, getDepartingSortListV2(FlightSortOptions.Cheapest))[0]
  return sortedFlight?.price.adult.totalFare
}

export function getFlightV2BestTime(journeys: Array<App.JourneyV2>): App.FlightGroup['totalFlightDuration'] | undefined {
  const sortedFlight = sortByAll(journeys, getDepartingSortListV2(FlightSortOptions.Fastest))[0]

  return sortedFlight?.flightGroup.totalFlightDuration
}

export function isSameFlightTime(timeA?: App.FlightGroup['totalFlightDuration'], timeB?: App.FlightGroup['totalFlightDuration']): boolean {
  return timeA?.hours === timeB?.hours && timeA?.minutes === timeB?.minutes
}

interface CheckoutFlightQueryParams {
  departDate?: string | null
  returnDate?: string | null
  originAirportCode?: string | null
  destinationAirportCode?: string | null
}

export function getFlightCheckoutQueryParams(url: string = ''): CheckoutFlightQueryParams | undefined {
  const pageParams = new URLSearchParams(window.location.search)
  const query = new URLSearchParams(url.split('?')[1])

  const departDate = query.get('departDate') || pageParams.get('departDate')
  const returnDate = query.get('returnDate') || pageParams.get('returnDate')
  const originAirportCode = query.get('originAirportCode') || pageParams.get('originAirportCode')
  const destinationAirportCode = query.get('destinationAirportCode') || pageParams.get('destinationAirportCode')
  if (departDate && returnDate && originAirportCode && destinationAirportCode) {
    return { departDate, destinationAirportCode, originAirportCode, returnDate }
  }
}

export function getFlightCheckoutQueryParamsFromFlightItem(item: App.Checkout.FlightItem): CheckoutFlightQueryParams | undefined {
  const {
    travelStart: departDate,
    travelEnd: returnDate,
    originAirportCode,
    destinationAirportCode,
  } = item
  if (departDate && returnDate && originAirportCode && destinationAirportCode) {
    return { departDate, destinationAirportCode, originAirportCode, returnDate }
  }
}

const FLIGHTS_SESSION_LAST_SEARCH_KEY = 'flight-session-last-search'
const FLIGHTS_RECENT_SEARCHES_KEY = 'flight-session-recent-searches'

export interface LastSearchSessionData {
  flights: Array<{
    departureAirport?: App.AirportLocation
    arrivalAirport?: App.AirportLocation
    departingDate?: string | null
    returningDate?: string | null
  }>
  fareType?: string
  fareClass?: string
  occupants?: App.Occupants
}

interface LastSearchSessionDataResponse extends LastSearchSessionData {
  searchedOn: number
}

export function setFlightSessionRecentSearch(data: LastSearchSessionData) {
  const withTimestamp = { ...data, searchedOn: Date.now() }
  isomorphicLocalStorage.set(FLIGHTS_SESSION_LAST_SEARCH_KEY, withTimestamp)

  const recent = getLocalRecentFlightSearches()
  const mapped = mapLastSearchSessionToRecent(withTimestamp)
  const seenKeys = new Set<string>()
  const next = [mapped, ...recent]
    .filter(s => {
      const k = recentSearchKey(s)
      if (seenKeys.has(k)) return false
      seenKeys.add(k)
      return true
    })
    .slice(0, 3)

  isomorphicLocalStorage.set(FLIGHTS_RECENT_SEARCHES_KEY, next)
}

export function getFlightsRecentSearch(): LastSearchSessionDataResponse | undefined {
  const recentSearch = isomorphicLocalStorage.get(FLIGHTS_SESSION_LAST_SEARCH_KEY)
  if (recentSearch) return recentSearch

  return undefined
}

function mapLastSearchSessionToRecent(last: LastSearchSessionDataResponse): App.RecentFlightSearch {
  const fareType = (last.fareType || '').toLowerCase()
  const tripType = fareType === FlightsFareTypes.ONE_WAY
    ? 'OneWay'
    : fareType === FlightsFareTypes.MULTI_CITY
      ? 'MultiCity'
      : 'Return'

  const baseLegs: Array<App.RecentFlightSearchItineraryLeg> = (last.flights || []).map(f => ({
    departureAirport: f.departureAirport?.airportCode || '',
    departureDate: f.departingDate ? moment(f.departingDate).format('DD/MM/YYYY') : '',
    arrivalAirport: f.arrivalAirport?.airportCode || '',
    departureAirportName: f.departureAirport?.airportName || '',
    arrivalAirportName: f.arrivalAirport?.airportName || '',
    departureCityDesc: '',
    arrivalCityDesc: '',
  }))

  let itinerary: Array<App.RecentFlightSearchItineraryLeg> = baseLegs

  // For return fares, append a synthetic return leg using returningDate if available
  if (tripType === 'Return' && last.flights?.[0]?.returningDate) {
    const first = last.flights[0]
    itinerary = [
      baseLegs[0],
      {
        departureAirport: first.arrivalAirport?.airportCode || '',
        departureDate: moment(first.returningDate).format('DD/MM/YYYY'),
        arrivalAirport: first.departureAirport?.airportCode || '',
        departureAirportName: first.arrivalAirport?.airportName || '',
        arrivalAirportName: first.departureAirport?.airportName || '',
        departureCityDesc: '',
        arrivalCityDesc: '',
      },
    ]
  }

  return {
    maxArrivalTime: undefined,
    minReturningDepartureTime: undefined,
    source: 'local',
    fareClass: last.fareClass,
    passengers: {
      numAdults: last.occupants?.adults || 1,
      numChildren: last.occupants?.children || 0,
      numInfants: last.occupants?.infants || 0,
    },
    tripType,
    sessionId: String(last.searchedOn || Date.now()),
    searchId: String(last.searchedOn || Date.now()),
    itinerary,
  }
}

function recentSearchKey(s: App.RecentFlightSearch): string {
  return [
    s.tripType,
    s.fareClass,
    s.passengers.numAdults,
    s.passengers.numChildren,
    s.passengers.numInfants,
    ...s.itinerary.map(it => `${it.departureAirport}-${it.arrivalAirport}-${it.departureDate}`),
  ].join('|')
}

export function getLocalRecentFlightSearches(): Array<App.RecentFlightSearch> {
  const recent = isomorphicLocalStorage.get(FLIGHTS_RECENT_SEARCHES_KEY)
  return Array.isArray(recent) ? recent : []
}

export function bumpRecentFlightSearch(search: App.RecentFlightSearch) {
  try {
    const current = getLocalRecentFlightSearches()
    const key = recentSearchKey(search)
    const deduped = current.filter(s => recentSearchKey(s) !== key)
    const next = [search, ...deduped].slice(0, 3)
    isomorphicLocalStorage.set(FLIGHTS_RECENT_SEARCHES_KEY, next)
  } catch {
    /* noop */
  }
}

export function getCartFlightItemsIds(cartItems: Array<App.Checkout.AnyItem> = []): Array<string> {
  return cartItems.reduce((acc: Array<string>, item) => {
    if (item.itemType === 'flight') {
      return [...acc, ...item.flights.map(flight => flight.journeyKey)].filter(Boolean)
    }

    return acc
  }, [])
}

export function getFlightSummaryDetails(flight: App.Flight) {
  const route = `${capitalise(flight.departingDisplayNames.primary)} to ${capitalise(flight.arrivalDisplayNames.primary)}`
  const departDateTime = moment(`${flight.departingDate} ${flight.departingTime}`).format(SHORT_DATE_TIME_FORMAT)
  const arrivalDateTime = moment(`${flight.arrivalDate} ${flight.arrivalTime}`).format(SHORT_DATE_TIME_FORMAT)

  return {
    route,
    dateTime: `${departDateTime} - ${arrivalDateTime}`,
  }
}

export function isDepartingSegment(segments: Array<string>, activeSegment?: string) {
  if (activeSegment && segments[0]) {
    return activeSegment === segments[0]
  }

  return false
}

export function isReturningSegment(segments: Array<string>, activeSegment?: string) {
  if (activeSegment && segments[1]) {
    return activeSegment === segments[1]
  }

  return false
}

export function serialiseFlightsOccupancy(occupants: Array<App.Occupants>): Array<string> {
  return occupants.map(occupant => {
    const occupancy = [occupant.adults.toString(), (occupant.children || 0).toString(), (occupant.infants || 0).toString()]

    if (occupant.childrenAge?.length) {
      occupancy.push(`${occupant.childrenAge.join(',')}`)
    }

    return occupancy.join('-')
  })
}

export function getFlightSearchQueryparams(items: Array<FlightSearchWidgetFlightItem>, fareType: FlightsFareTypes) {
  if (fareType === FlightsFareTypes.MULTI_CITY && items.length > 1) {
    return {
      flights: items.map(item => {
        const departDate = moment(item.checkinDate).format(ISO_DATE_FORMAT)

        return {
          originAirportCode: item.departureAirport?.airportCode,
          originAirportName: item.departureAirport?.airportName,
          destinationAirportCode: item.arrivalAirport?.airportCode,
          destinationAirportName: item.arrivalAirport?.airportName,
          departDate,
        }
      }),
    }
  }

  const flight = items[0]

  const isReturnFare = fareType === FlightsFareTypes.RETURN
  const departDate = moment(flight.checkinDate).format(ISO_DATE_FORMAT)
  const returnDate = isReturnFare ? moment(flight.checkoutDate).format(ISO_DATE_FORMAT) : null

  return {
    originAirportCode: flight.departureAirport?.airportCode,
    originAirportName: flight.departureAirport?.airportName,
    destinationAirportCode: flight.arrivalAirport?.airportCode,
    destinationAirportName: flight.arrivalAirport?.airportName,
    departingQuotedPrice: flight.checkinQuotedPrice,
    returningQuotedPrice: flight.checkoutQuotedPrice,
    departDate,
    returnDate,
  }
}

export function isStandaloneFlightsCart(items: Array<App.Checkout.AnyItem>) {
  const hasFlight = items.some(isFlightItem)
  const hasBookingProtection = items.some(isInsuranceBookingProtectionItem)
  const onlyFlights = items.length === 1 && hasFlight
  const flightsAndBooking = items.length === 2 && hasFlight && hasBookingProtection
  return onlyFlights || flightsAndBooking
}

export function getCarrierInfoFromLegs(flights: Array<App.LegFlight>): { message?: string, carrierLogo: string, carrierName: string } {
  const operatingAirlines = flights.map(flight => flight.operatingAirline.name)

  const marketingCarrier = {
    carrierLogo: flights[0]?.marketingAirline.logoUrl,
    carrierName: flights[0]?.marketingAirline.name,
    carrierBrandMark: flights[0]?.marketingAirline.brandMarkUrl,
  }

  return getOperatedByMessage(operatingAirlines, marketingCarrier)
}

export function getCarrierInfoFromJourney(journey: App.JourneyV2): {
  message: string
  carrierLogo: string
  carrierName: string
  carrierBrandMark?: string
} {
  const flights = journey.flightGroup.flights
  const operatingAirlines = flights.map(flight => flight.operatingAirline.name)
  const codeShareDetails = flights.map(flight => flight.codeShareDetails).filter((detail): detail is string => Boolean(detail))

  const marketingCarrier = {
    carrierLogo: journey.carrierLogo,
    carrierName: journey.carrierName,
    carrierBrandMark: journey.carrierBrandMark,
  }

  return getOperatedByMessage(operatingAirlines, marketingCarrier, codeShareDetails)
}

function getOperatedByMessage(
  operatingAirlines: Array<string>,
  marketingCarrier: { carrierLogo: string, carrierName: string },
  codeShareDetails?: Array<string>,
): { message: string, carrierLogo: string, carrierName: string } {
  const uniqueOperatingAirlines = new Set<string>(operatingAirlines)
  const noMarketingAirlines = new Set<string>(operatingAirlines.filter(airline => airline !== marketingCarrier.carrierName))
  const noMarketingAirlinesNames = noMarketingAirlines.values()
  const uniqueCodeShareDetails = new Set<string>(codeShareDetails)

  const message = marketingCarrier.carrierName

  if (uniqueOperatingAirlines.size === 1 && uniqueCodeShareDetails?.size) {
    return {
      message: `${message} · ${uniqueCodeShareDetails.values().next().value}`,
      ...marketingCarrier,
    }
  }

  if (uniqueOperatingAirlines.size === 1 && noMarketingAirlines.size === 1) {
    return {
      message: `${message} · Operated by ${noMarketingAirlinesNames.next().value}`,
      ...marketingCarrier,
    }
  }

  if (uniqueOperatingAirlines.size === 2 && noMarketingAirlines.size === 1) {
    return {
      message: `${message} · Partially operated ${noMarketingAirlinesNames.next().value}`,
      ...marketingCarrier,
    }
  }
  if (noMarketingAirlines.size >= 2) {
    return {
      message: `${message} · Operated by multiple airlines`,
      ...marketingCarrier,
    }
  }

  return {
    message,
    ...marketingCarrier,
  }
}

export function formatFlightRelativePrice(num: number) {
  const roundedNum = Math.round(num)
  // eslint-disable-next-line no-compare-neg-zero
  return Math.sign(roundedNum) === -0 && roundedNum === 0 ? 0 : roundedNum
}

export function isLegFlight(flight?: App.LegFlight | App.FlightV2): flight is App.LegFlight {
  return !!flight && 'flightUniqueId' in flight
}

export const SPECIAL_DESTINATION_NAMES = {
  KL: 'Kuala Lumpur',
  Rio: 'Rio de Janeiro',
} as const

type SpecialDestinationKey = keyof typeof SPECIAL_DESTINATION_NAMES

export const adjustDestinationName = (destinationName: string): string => {
  const adjustedName = destinationName.replace(' All Airports', '')
  return (SPECIAL_DESTINATION_NAMES[adjustedName as SpecialDestinationKey] || adjustedName)
}

function getDomesticLoyaltyProductType(fareClass: string) {
  if (fareClass === 'business') return 'dom_business'
  return 'dom_economy'
}

function getInternationalLoyaltyProductType(fareClass: string): App.LoyaltyFlightProductType {
  if (fareClass === 'economy') return 'int_economy'
  if (fareClass === 'premium') return 'int_premium_economy'
  if (fareClass === 'business') return 'int_business'
  if (fareClass === 'first') return 'int_first'
  return 'int_economy'
}

export function getFlightLoyaltyProductType(fareClass: string, domestic: boolean): App.LoyaltyFlightProductType {
  if (domestic) return getDomesticLoyaltyProductType(fareClass)
  return getInternationalLoyaltyProductType(fareClass)
}

export function getFlightLoyaltyProductTypeRank(type: App.LoyaltyFlightProductType): number {
  if (type === 'dom_business' || type === 'int_business') return 1
  if (type === 'int_first') return 2
  if (type === 'int_premium_economy') return 3
  return 4
}

export function isFlightJourneyDomestic(flights: Array<App.FlightV2>) {
  const country = flights[0]?.departureCountry
  return flights.every((flight) => flight.departureCountry === flight.arrivalCountry && flight.departureCountry === country)
}

export const TITLE_MAPPER: Record<FlightsFareTypes, string> = {
  [FlightsFareTypes.ONE_WAY]: 'One-way flight',
  [FlightsFareTypes.RETURN]: 'Return flights',
  [FlightsFareTypes.MULTI_CITY]: 'Multi-city flights',
}

export function isCheckedBaggageIncluded(journey?: App.JourneyV2) {
  return journey?.flightGroup.flights[0].includedCheckedBaggage?.includedInFare ||
    journey?.fareFamily?.fareFeatures.find(feature => feature.type === 'checked_baggage')?.status === 'INCLUDED'
}

export function isCarryOnBaggageIncluded(journey?: App.JourneyV2) {
  return journey?.flightGroup.flights[0].includedCarryOnBaggage?.includedInFare ||
    journey?.fareFamily?.fareFeatures.find(feature => ['carry_on_bag', 'carry_on_baggage'].includes(feature.type || ''))?.status === 'INCLUDED'
}

export function getFlightsTotal(item: App.Checkout.FlightItem) {
  if (item.flights[0].fareFamily) {
    const isReturnFares = item.viewType === FlightViewTypes.TWO_ONE_WAYS_AND_RETURN

    if (isReturnFares) {
      const [departing, returning] = item.flights
      if (departing && returning) {
        return (departing.fareFamily?.cost?.all ?? 0) + (returning.fareFamily?.cost?.all ?? 0)
      }
      return departing?.fareFamily?.cost?.all ?? 0
    }

    return item.flights.reduce((sum, flight) => sum + (flight.fareFamily?.cost?.all ?? 0), 0)
  }

  return item.flights.reduce((sum, flight) => sum + (flight.cost ?? 0), 0)
}

function hasBaggage(fareFamily: App.FlightFareFamily) {
  return fareFamily.fareFamily.fareFeatures.some(
    (feature: App.FareFamilyFeature) =>
      feature.category === 'baggage' &&
      feature.type === 'checked_baggage' &&
      feature.status === 'INCLUDED',
  )
}

function isRefundable(fareFamily: App.FlightFareFamily) {
  return fareFamily.fareFamily.fareFeatures.some(
    (feature: App.FareFamilyFeature) =>
      feature.category === 'fare_rules' &&
      feature.type === 'refunds' &&
      feature.status === 'INCLUDED',
  )
}

interface CheapestEconomyFareFamilyWithBaggageReturn {
  cheapestEconomyWithBaggage: App.FlightFareFamily | null
  pairedReturningFareFamily: App.FlightFareFamily | null
  journeyTotalCost: number
}

export function isFareFamilyInsuranceEligible(fareFamily: App.FlightFareFamily) {
  return hasBaggage(fareFamily) && !isRefundable(fareFamily) && fareFamily.cabin.toLowerCase() === FlightsClassTypes.ECONOMY
}

export function getCheapestEconomyFareFamilyWithBaggage(fareFamilies: Array<App.FlightFareFamily>, isReturnType: boolean = false): CheapestEconomyFareFamilyWithBaggageReturn {
  if (!Array.isArray(fareFamilies)) return { cheapestEconomyWithBaggage: null, pairedReturningFareFamily: null, journeyTotalCost: 0 }
  const fareFamiliesWithBaggage = fareFamilies.filter(family => {
    const shouldInclude = isFareFamilyInsuranceEligible(family)

    if (isReturnType) {
      // Fare Families without pairedUpsellIds are filtered out in upsell modal, so we need to ignore them too here.
      return shouldInclude && Array.isArray(family.pairedUpsellIds) && family.pairedUpsellIds.length > 0
    }

    return shouldInclude
  })
  const cheapestEconomyWithBaggage = fareFamiliesWithBaggage.length ?
      fareFamiliesWithBaggage.reduce((min, curr) =>
        curr.cost.all < min.cost.all ? curr : min,
      ) :
    null

  if (!cheapestEconomyWithBaggage) return { cheapestEconomyWithBaggage: null, pairedReturningFareFamily: null, journeyTotalCost: 0 }
  if (!isReturnType) return { cheapestEconomyWithBaggage, pairedReturningFareFamily: null, journeyTotalCost: cheapestEconomyWithBaggage.cost.all }

  const pairedUpsellId = cheapestEconomyWithBaggage.pairedUpsellIds?.[0]
  const pairedReturningFareFamily = pairedUpsellId ? fareFamilies.find(family => family.id === pairedUpsellId) : null

  if (!pairedReturningFareFamily) return { cheapestEconomyWithBaggage: null, pairedReturningFareFamily: null, journeyTotalCost: 0 }

  return { cheapestEconomyWithBaggage, pairedReturningFareFamily, journeyTotalCost: cheapestEconomyWithBaggage.cost.all + pairedReturningFareFamily.cost.all }
}

export function cloneWithInsurance(family: App.FlightFareFamily, quote: App.InsuranceQuote, occupancies: App.Occupants | undefined, shouldDivideQuoteByTwo: boolean = false, idSuffix?: string) {
  const totalPerPerson = shouldDivideQuoteByTwo ? quote.totalPerPerson / 2 : quote.totalPerPerson
  const total = shouldDivideQuoteByTwo ? quote.total / 2 : quote.total
  return {
    ...family,
    costWithInsurance: {
      adult: family.cost.adult + (occupancies?.adults ? totalPerPerson : 0),
      children: family.cost.children + (occupancies?.children ? totalPerPerson : 0),
      infant: family.cost.infant + (occupancies?.infants ? totalPerPerson : 0),
      all: family.cost.all + total,
    },
    withEmbeddedInsurance: true,
    id: `${family.id}${FLIGHT_INSURANCE_ID_PREFIX}${idSuffix ?? '1'}`,
  }
}

export function getFeaturesByFareFamily(fareFamilies: Array<App.FlightFareFamily>) {
  const allFeatures = uniqueBy(fareFamilies.flatMap(family => family.fareFamily.fareFeatures), feature => feature.group)

  return arrayToMap(
    fareFamilies,
    family => family.id,
    family => {
      const featuresByCode = arrayToMap(family.fareFamily.fareFeatures, feature => feature.group)
      const featuresByFareFamily = allFeatures.map(feature => ({
        ...(featuresByCode.get(feature.group) ?? feature),
        status: featuresByCode.get(feature.group)?.status ?? 'NOT_OFFERED',
      }))

      if (family.withEmbeddedInsurance) {
        const filteredFeaturesByFareFamily = featuresByFareFamily.filter(({ description }) => !description.match(/(?:refunds?|cancellations?)/i))
        return [
          { status: 'INCLUDED', code: 'insurance', description: 'No extra fees for claims', type: 'insurance', group: 0, category: 'fare_rules' } as App.FareFamilyFeature,
          ...filteredFeaturesByFareFamily,
        ]
      }

      return featuresByFareFamily
    },
  )
}

export function hasCheckoutFlightItemsWithEmbeddedInsurance(flightItems: Array<App.Checkout.FlightItem>) {
  return flightItems.some(item => item.flights.some(flight => flight.fareFamily?.withEmbeddedInsurance || flight.withEmbeddedInsurance))
}

interface NameAdjustmentParams {
  firstName: string
  middleName: string
  lastName: string
  nameAdjustmentType: string | undefined
  maxTotalLength: number | undefined
}

export enum NameAdjustmentType {
  REMOVE_MIDDLE_NAME = 'REMOVE_MIDDLE_NAME',
  ABBREVIATE_MIDDLE_NAME = 'ABBREVIATE_MIDDLE_NAME',
  REMOVE_APOSTROPHE = 'REMOVE_APOSTROPHE',
  REMOVE_HYPHEN = 'REMOVE_HYPHEN',
  REMOVE_PERIOD = 'REMOVE_PERIOD',
  REPLACE_DIACRITICS = 'REPLACE_DIACRITICS',
}

interface NameAdjustmentResult {
  name: string
  adjustments: Array<NameAdjustmentType>
}

interface FullNameAdjustmentResult {
  firstName: string
  middleName: string
  lastName: string
  title?: string
  firstNameAdjustments: Array<string>
  middleNameAdjustments: Array<string>
  lastNameAdjustments: Array<string>
}

// Map of diacritics to their standard equivalents
const baseDiacriticsMap: Record<string, string> = {
  À: 'A',
  Á: 'A',
  Â: 'A',
  Ã: 'A',
  Ä: 'AE',
  Å: 'AA',
  Æ: 'AE',
  Ç: 'C',
  È: 'E',
  É: 'E',
  Ê: 'E',
  Ë: 'E',
  Ì: 'I',
  Í: 'I',
  Î: 'I',
  Ï: 'I',
  Ð: 'D',
  Ñ: 'N',
  Ò: 'O',
  Ó: 'O',
  Ô: 'O',
  Õ: 'O',
  Ö: 'OE',
  Ø: 'OE',
  Ù: 'U',
  Ú: 'U',
  Û: 'U',
  Ü: 'UE',
  Ý: 'Y',
  Þ: 'TH',
  Ā: 'A',
  Ă: 'A',
  Ą: 'A',
  Ć: 'C',
  Ĉ: 'C',
  Ċ: 'C',
  Č: 'C',
  Ď: 'D',
  Đ: 'D',
  Ē: 'E',
  Ĕ: 'E',
  Ė: 'E',
  Ę: 'E',
  Ě: 'E',
  Ĝ: 'G',
  Ğ: 'G',
  Ġ: 'G',
  Ģ: 'G',
  Ĥ: 'H',
  Ħ: 'H',
  Ĩ: 'I',
  Ī: 'I',
  Ĭ: 'I',
  Į: 'I',
  İ: 'I',
  ı: 'I',
  Ĳ: 'IJ',
  Ĵ: 'J',
  Ķ: 'K',
  Ĺ: 'L',
  Ļ: 'L',
  Ľ: 'L',
  Ŀ: 'L',
  Ł: 'L',
  Ń: 'N',
  Ņ: 'N',
  Ň: 'N',
  Ŋ: 'N',
  Ō: 'O',
  Ŏ: 'O',
  Ő: 'O',
  Œ: 'OE',
  Ŕ: 'R',
  Ŗ: 'R',
  Ř: 'R',
  Ś: 'S',
  Ŝ: 'S',
  Ş: 'S',
  Š: 'S',
  Ţ: 'T',
  Ť: 'T',
  Ŧ: 'T',
  Ũ: 'U',
  Ū: 'U',
  Ŭ: 'U',
  Ů: 'U',
  Ű: 'U',
  Ų: 'U',
  Ŵ: 'W',
  Ŷ: 'Y',
  Ÿ: 'Y',
  Ź: 'Z',
  Ż: 'Z',
  Ž: 'Z',
  ẞ: 'SS',
}

// Extend the base diacritics map with lowercase equivalents
function extendWithLowercase(map: Record<string, string>): Record<string, string> {
  const extended: Record<string, string> = { ...map }
  for (const [char, replacement] of Object.entries(map)) {
    const lowerChar = char.toLowerCase()
    if (!extended[lowerChar]) {
      extended[lowerChar] = replacement.toLowerCase()
    }
  }
  return extended
}

const diacriticsMap: Record<string, string> = extendWithLowercase(baseDiacriticsMap)

export function adjustMiddleName({
  firstName,
  middleName,
  lastName,
  nameAdjustmentType,
  maxTotalLength,
}: NameAdjustmentParams) {
  const totalLength = firstName.length + (middleName?.length || 0) + lastName.length

  if (maxTotalLength === undefined || !middleName) {
    return middleName
  }

  // We don't need to apply custom adjustments to the middle name if the total length is less than the max total length
  if (totalLength <= maxTotalLength) {
    return middleName
  }

  if (nameAdjustmentType === NameAdjustmentType.REMOVE_MIDDLE_NAME) {
    return ''
  } else if (nameAdjustmentType === NameAdjustmentType.ABBREVIATE_MIDDLE_NAME) {
    const currentTotalLengthWithoutMiddle = firstName.length + lastName.length
    const middleNames = middleName.split(' ')
    for (let i = middleNames.length - 1; i >= 0; i--) {
      middleNames[i] = middleNames[i].slice(0, 1)
      if (currentTotalLengthWithoutMiddle + middleNames.join(' ').length <= maxTotalLength) {
        break
      }
    }
    return middleNames.join(' ')
  }

  return middleName
}

export function formatFlightTravellerName(traveller: App.FlightTraveller): string {
  const title = titles?.[traveller.type]?.[traveller.title] ?? traveller.title ?? ''
  return `${title} ${traveller.given_name} ${traveller.surname}`.trim()
}

export function formatFlightTravellerNameWithoutTitle(traveller: App.FlightTraveller): string {
  return `${traveller.given_name} ${traveller.middle_name} ${traveller.surname}`.trim()
}

export function formatFlightTravellerNameInitials(traveller: App.FlightTraveller, isFirstLetterOnly: boolean = false): string {
  if (isFirstLetterOnly) {
    return traveller.given_name.charAt(0)
  }
  return traveller.given_name.charAt(0) + traveller.surname.charAt(0)
}

export function adjustName(name: string): NameAdjustmentResult {
  const adjustments: Array<NameAdjustmentType> = []
  let adjustedName = name

  if (adjustedName.includes("'")) {
    adjustedName = adjustedName.replace(/'/g, '')
    adjustments.push(NameAdjustmentType.REMOVE_APOSTROPHE)
  }

  if (adjustedName.includes('-')) {
    adjustedName = adjustedName.replace(/-/g, ' ')
    adjustments.push(NameAdjustmentType.REMOVE_HYPHEN)
  }

  if (adjustedName.includes('.')) {
    adjustedName = adjustedName.replace(/\./g, ' ')
    adjustments.push(NameAdjustmentType.REMOVE_PERIOD)
  }

  let hasDiacritics = false
  let normalizedName = ''
  for (let i = 0; i < adjustedName.length; i++) {
    const char = adjustedName[i]
    if (diacriticsMap[char]) {
      normalizedName += diacriticsMap[char]
      hasDiacritics = true
    } else {
      normalizedName += char
    }
  }

  if (hasDiacritics) {
    adjustedName = normalizedName
    adjustments.push(NameAdjustmentType.REPLACE_DIACRITICS)
  }

  return {
    name: adjustedName,
    adjustments,
  }
}

/**
 * Adjusts a complete passenger name, normalizing special characters and handling length constraints
 *
 * @param params Object containing title, firstName, middleName, lastName, nameAdjustmentType and maxTotalLength
 * @returns Object containing adjusted name parts and their adjustment details
 */
export function adjustFullName({
  title,
  firstName,
  middleName,
  lastName,
  nameAdjustmentType,
  maxTotalLength,
}: {
  title?: string
  firstName: string
  middleName: string
  lastName: string
  nameAdjustmentType?: string
  maxTotalLength?: number
}): FullNameAdjustmentResult {
  // First normalize all name parts to handle special characters and diacritics
  const adjustedFirstName = adjustName(firstName)
  const adjustedLastName = adjustName(lastName)
  const adjustedMiddleName = adjustName(middleName)

  // Then apply the length adjustment to the middle name if needed
  const finalMiddleName = adjustMiddleName({
    firstName: adjustedFirstName.name,
    middleName: adjustedMiddleName.name,
    lastName: adjustedLastName.name,
    nameAdjustmentType,
    maxTotalLength,
  })

  // If middle name was further adjusted for length, add that to its adjustments
  const middleNameAdjustments = [...adjustedMiddleName.adjustments]
  if (finalMiddleName !== adjustedMiddleName.name) {
    if (finalMiddleName === '') {
      middleNameAdjustments.push(NameAdjustmentType.REMOVE_MIDDLE_NAME)
    } else if (finalMiddleName.length < adjustedMiddleName.name.length) {
      middleNameAdjustments.push(NameAdjustmentType.ABBREVIATE_MIDDLE_NAME)
    }
  }

  return {
    title,
    firstName: adjustedFirstName.name,
    middleName: finalMiddleName,
    lastName: adjustedLastName.name,
    firstNameAdjustments: adjustedFirstName.adjustments,
    middleNameAdjustments,
    lastNameAdjustments: adjustedLastName.adjustments,
  }
}

/**
 * Maps FlightPassenger types to FlightSeatTraveler types
 */
const mapPassengerTypeToSeatType = (type: 'adult' | 'child' | 'infant'): 'ADT' | 'CHD' | 'INF' => {
  switch (type) {
    case 'adult':
      return 'ADT'
    case 'child':
      return 'CHD'
    case 'infant':
      return 'INF'
    default:
      return 'ADT'
  }
}

/**
 * Converts FlightPassenger array to FlightSeatTraveler array
 */
export function mapPassengersToSeatTravelers(passengers: Array<App.Checkout.FlightPassenger>): Array<{ id: string; type: 'ADT' | 'CHD' | 'INF' }> {
  return passengers.map(passenger => ({
    id: passenger.id,
    type: mapPassengerTypeToSeatType(passenger.type)
  }))
}

/** This is not a good mapping, avoid using it if you can */
export function airportLocationToAirport(airportLocation: App.AirportLocation): App.Airport {
  return {
    code: airportLocation.airportCode,
    name: airportLocation.airportName,
    latitude: airportLocation.lt ?? 0,
    longitude: airportLocation.lg ?? 0,
  }
}

/**
 * Parses weight in kg from a baggage description string.
 * Returns undefined if no valid weight is found.
 * @example parseWeightInKg("7kg carry-on baggage") => 7
 * @example parseWeightInKg("Carry-on baggage") => undefined
 */
export function parseWeightInKg(description?: string | null): number | undefined {
  if (!description) {
    return undefined
  }
  const match = description.match(/(\d+(?:\.\d+)?)\s?kg/i)
  if (!match) {
    return undefined
  }
  const parsed = Number(match[1])
  return Number.isFinite(parsed) ? parsed : undefined
}

/**
 * Parses weight and count from baggage descriptions like "2x 7kg", "7kg 2x", or "7kg"
 * @returns { count: number, weightPerItemKg: number, totalWeightKg: number } or undefined if parsing fails
 */
export function parseWeightWithCount(description?: string | null): { count: number; weightPerItemKg: number; totalWeightKg: number } | undefined {
  if (!description) {
    return undefined
  }

  // Match patterns like "2x 7kg"
  const matchCountFirst = description.match(/(\d+)\s?x\s?(\d+(?:\.\d+)?)\s?kg/i)
  if (matchCountFirst) {
    const count = Number(matchCountFirst[1])
    const weightPerItem = Number(matchCountFirst[2])
    if (Number.isFinite(count) && Number.isFinite(weightPerItem)) {
      return {
        count,
        weightPerItemKg: weightPerItem,
        totalWeightKg: count * weightPerItem,
      }
    }
  }

  // Match patterns like "7kg 2x"
  const matchWeightFirst = description.match(/(\d+(?:\.\d+)?)\s?kg\s?(\d+)\s?x/i)
  if (matchWeightFirst) {
    const weightPerItem = Number(matchWeightFirst[1])
    const count = Number(matchWeightFirst[2])
    if (Number.isFinite(count) && Number.isFinite(weightPerItem)) {
      return {
        count,
        weightPerItemKg: weightPerItem,
        totalWeightKg: count * weightPerItem,
      }
    }
  }

  // Fallback to single weight without count
  const weight = parseWeightInKg(description)
  if (weight !== undefined) {
    return {
      count: 1,
      weightPerItemKg: weight,
      totalWeightKg: weight,
    }
  }

  return undefined
}
